import React, { useState, useRef } from "react";
import { Modal, Button, Form, Table, Spinner } from "react-bootstrap";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useFormik } from "formik";
import * as yup from "yup";
import { useAuth } from "../../hooks/useAuth";
import DateTime from "react-datetime";
import ConfirmDialog from "../ConfirmDialogue";
import CachedIcon from "mdi-react/CachedIcon";
import { DropdownCloseIcon } from "../Icons";

function Attachments({ files = [], setFiles, milestone, viewOnly = false }) {
  const { backendUrl } = useAuth();
  return (
    <div>
      <div>
        {files?.map((el, index) => (
          <div key={index} className="d-flex align-items-center">
            {el?.fileId ? (
              <a
                href={`${backendUrl}/api/equipment-certification/files/${
                  el.systemFileName
                }`}
                target="_blank"
                rel="noreferrer"
                className="text-primary"
              >
                {el?.name}
              </a>
            ) : (
              <span>{el?.name}</span>
            )}

            {!viewOnly && (
              <Button
                variant=""
                size="sm"
                onClick={(e) => setFiles(files.filter((el, i) => i !== index))}
              >
                <DropdownCloseIcon width={16} />
              </Button>
            )}
            <hr className="m-0" />
          </div>
        ))}
      </div>

      {!viewOnly && (
        <Form.Label className="btn btn-light-blue">
          <span>Add Files </span>
          <Form.Control
            type="file"
            className="d-none"
            multiple
            onChange={(e) => setFiles([...files, ...e.target.files])}
          />
        </Form.Label>
      )}
    </div>
  );
}

export default function EquipmentCertificationModal({
  show,
  onHide,
  data,
  hideAdd,
}) {
  const { backendUrl } = useAuth();
  const [editing, setEditing] = useState(null);
  const queryClient = useQueryClient();

  // Fetch certifications
  const barCode = data && data.Bar_Code ? data.Bar_Code : "";
  const { data: certificationsRaw, isLoading, isFetching, refetch } = useQuery(
    ["equipment-certifications", barCode],
    async () => {
      if (!barCode) return [];
      const res = await fetch(
        `${backendUrl}/api/equipment-certification?Bar_Code=${barCode}`,
      );
      const data = await res.json();
      // Defensive: always return an array
      if (Array.isArray(data)) return data;
      if (!data) return [];
      // If API returns an object, try to extract array from a known property
      if (typeof data === "object") {
        if (Array.isArray(data.results)) return data.results;
        if (Array.isArray(data.data)) return data.data;
        return [];
      }
      return [];
    },
    { enabled: show && !!barCode },
  );
  const certifications = Array.isArray(certificationsRaw)
    ? certificationsRaw
    : [];

  // Add certification
  const addMutation = useMutation(
    async (payload) => {
      const formData = new FormData();
      for (const [key, value] of Object.entries(payload)) {
        if (key === "files" && Array.isArray(value)) {
          for (const file of value) {
            if (!file?.fileId) {
              formData.append("files", file);
            }
          }
          continue;
        }
        formData.append(key, value);
      }
      return fetch(`${backendUrl}/api/equipment-certification`, {
        method: "POST",
        body: formData,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["equipment-certifications"]);
        formik.resetForm({
          values: {
            Bar_Code: data?.Bar_Code || "",
            Title: "",
            expiresAt: "",
            files: [],
          },
        });
        setSelectedFiles([]);
      },
    },
  );

  // Update certification
  const updateMutation = useMutation(
    async ({ id, payload, alreadyUploadedFiles }) => {
      const formData = new FormData();
      for (const [key, value] of Object.entries(payload)) {
        if (key === "files" && Array.isArray(value)) {
          for (const file of value) {
            if (!file?.fileId) {
              formData.append("files", file);
            }
          }
          continue;
        }
        formData.append(key, value);
      }
      // Add already uploaded files as JSON string
      formData.append(
        "alreadyUploadedFiles",
        JSON.stringify(alreadyUploadedFiles || []),
      );
      return fetch(`${backendUrl}/api/equipment-certification/${id}`, {
        method: "PUT",
        body: formData,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["equipment-certifications"]);
        setEditing(null);
        formik.resetForm({
          values: {
            Bar_Code: data?.Bar_Code || "",
            Title: "",
            expiresAt: "",
            files: [],
          },
        });
        setSelectedFiles([]);
      },
    },
  );

  // Delete certification
  const deleteMutation = useMutation(
    async (id) => {
      return fetch(`${backendUrl}/api/equipment-certification/${id}`, {
        method: "DELETE",
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["equipment-certifications"]);
      },
    },
  );

  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef();

  const formik = useFormik({
    initialValues: {
      Bar_Code: data?.Bar_Code || "",
      Title: "",
      expiresAt: "",
      files: [],
    },
    validationSchema: yup.object().shape({
      Bar_Code: yup.string().required("Item code is required"),
      Title: yup.string().required("Title is required"),
      expiresAt: yup.string().required("Expiry date is required"),
      // files: yup.array().min(1, "At least one file is required"),
    }),
    onSubmit: (values, { resetForm }) => {
      const payload = { ...values, files: selectedFiles };
      if (editing) {
        // Only pass files that are new (no fileId) and already uploaded files
        const alreadyUploadedFiles = selectedFiles.filter((f) => f.fileId);
        updateMutation.mutate({
          id: editing.id,
          payload,
          alreadyUploadedFiles,
        });
      } else {
        addMutation.mutate(payload);
      }
    },
    enableReinitialize: true,
  });

  const handleEdit = (cert) => {
    setEditing(cert);
    // Parse attachments if present
    let attachments = [];
    try {
      attachments = Array.isArray(cert.attachments)
        ? cert.attachments
        : JSON.parse(cert.attachments || "[]");
    } catch {
      attachments = [];
    }
    formik.setValues({
      Bar_Code: cert.Bar_Code || "",
      Title: cert.Title || "",
      expiresAt: cert.expiresAt || "",
      files: attachments,
    });
    setSelectedFiles(attachments);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (id) => {
    if (
      await ConfirmDialog({
        title: "Confirm Deletion",
        message: "Are you sure you want to delete this certification?",
        confirmText: "Delete",
        cancelText: "Cancel",
      })
    ) {
      deleteMutation.mutate(id);
    }
  };

  const submitting = addMutation.isLoading || updateMutation.isLoading;
  const loading = isLoading || isFetching || deleteMutation.isLoading;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      fullscreen
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          Equipment Certification - {data.Item_Name} - {data.Serial_Number}{" "}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => refetch()}
            disabled={loading}
          >
            {loading ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <CachedIcon />
            )}
          </Button>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!hideAdd && (
          <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
            <Form.Group className="mb-3">
              <Form.Label>Item Code</Form.Label>
              <Form.Control
                name="Bar_Code"
                value={formik.values.Bar_Code}
                onChange={formik.handleChange}
                isInvalid={!!formik.errors.Bar_Code && formik.touched.Bar_Code}
                required
                disabled
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.Bar_Code}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                name="Title"
                value={formik.values.Title}
                onChange={formik.handleChange}
                isInvalid={!!formik.errors.Title && formik.touched.Title}
                required
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.Title}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3 col-md-4">
              <Form.Label>Expiry Date</Form.Label>

              <DateTime
                timeFormat={false}
                closeOnSelect={true}
                closeOnClickOutside={true}
                dateFormat="MMM DD, YYYY"
                inputProps={{
                  className: `date-input form-control`,
                  placeholder: "Select date",
                  readOnly: true,
                }}
                value={formik.values.expiresAt}
                onChange={(date) => {
                  formik.setFieldValue("expiresAt", date, true);
                }}
                key={formik.values.expiresAt}
                id={`expiresAt-${formik.values.expiresAt}`}
              />

              {formik.touched.expiresAt && !!formik.errors.expiresAt ? (
                <span className="custom-invalid-feedback">
                  {formik.errors.expiresAt}
                </span>
              ) : null}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Certification Files</Form.Label>
              <Attachments
                files={selectedFiles}
                setFiles={(files) => {
                  setSelectedFiles(files);
                  formik.setFieldValue("files", files);
                }}
                viewOnly={false}
              />
              {formik.touched.files && formik.errors.files ? (
                <div className="text-danger small">{formik.errors.files}</div>
              ) : null}
            </Form.Group>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <Spinner size="sm" animation="border" />
              ) : editing ? (
                "Update"
              ) : (
                "Add"
              )}
            </Button>
            {editing && (
              <Button
                variant="secondary"
                className="ms-2"
                onClick={() => {
                  setEditing(null);
                  formik.resetForm({
                    values: {
                      Bar_Code: data?.Bar_Code || "",
                      Title: "",
                      expiresAt: "",
                      files: [],
                    },
                  });
                }}
              >
                Cancel Edit
              </Button>
            )}
          </Form>
        )}
        <hr />

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <Table
            borderless
            hover
            striped
            className="product-table border"
            responsive
            size="sm"
          >
            <thead>
              <tr>
                <th>Item Code</th>
                <th>Title</th>
                <th>Expiry Date</th>
                <th>Post Date</th>
                <th>Files</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {certifications && certifications?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No certifications found.
                  </td>
                </tr>
              ) : (
                certifications &&
                certifications?.map((cert) => {
                  // Parse attachments (array of files)
                  let attachments = [];
                  try {
                    attachments = Array.isArray(cert.attachments)
                      ? cert.attachments
                      : JSON.parse(cert.attachments || "[]");
                  } catch {
                    attachments = [];
                  }
                  return (
                    <tr key={cert.id}>
                      <td>{cert.Bar_Code}</td>
                      <td>{cert.Title}</td>
                      <td>
                        {cert.expiresAt ? cert.expiresAt.split("T")[0] : ""}
                      </td>
                      <td>
                        {cert.createdAt ? cert.createdAt.split("T")[0] : ""}
                      </td>
                      <td>
                        {attachments.length > 0 ? (
                          attachments.map((file, idx) =>
                            file?.systemFileName ? (
                              <a
                                key={file.systemFileName + idx}
                                href={`${backendUrl}/api/equipment-certification/files/${
                                  file.systemFileName
                                }`}
                                target="_blank"
                                rel="noreferrer"
                                className="d-block text-primary"
                              >
                                {file.name ||
                                  file.originalFilename ||
                                  `File ${idx + 1}`}
                              </a>
                            ) : (
                              <span key={idx}>
                                {file.name ||
                                  file.originalFilename ||
                                  `File ${idx + 1}`}
                              </span>
                            ),
                          )
                        ) : (
                          <span className="text-muted">No files</span>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(cert)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDelete(cert.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
